import { Component } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/HouseEditForm/HouseEditForm.css'
import localStyles from 'components/features/pfm/HouseEditForm/HouseEditForm.styl'
import { Field, reduxForm, getFormValues } from 'redux-form'
import { ReduxFormProps } from 'interfaces/component'
import { HomePrice, HomeFormValues } from 'interfaces/property'
import { State } from 'interfaces/state'
import schema from 'schemas/homeSchema'
import { asyncValidator } from 'helpers/schemaHelpers'
import { getSaveButtonFormProps, parseValueToBoolean, shouldAsyncValidate } from 'helpers/formHelpers'
import { FORM_HOMES_EDIT } from 'constants/forms'
import Deferred from 'components/utils/Deferred'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import Button from 'components/features/pfm/Button/Button'
import CityStateField from 'components/atoms/inputs/CityStateField'
import SelectWrapped from 'components/atoms/inputs/OldSelectWrapped'
import CheckboxWrapped from 'components/atoms/inputs/CheckboxWrapped'
import HomePriceBalloon from 'components/atoms/balloons/HomePriceBalloon'
import {
  FIELD_ADDRESS1_PROPS,
  FIELD_ADDRESS2_PROPS,
  FIELD_ZIP_PROPS,
  FIELD_PURCHASED_PROPS,
  FIELD_HOME_ESTIMATED_PROPS,
  FIELD_PRIMARY_RESIDENCE_PROPS,
  FIELD_CURRENCY_WRAPPED_PROPS,
} from 'constants/fields'
import { getHomePriceKey } from 'helpers/propertyHelpers'
import { connect } from 'react-redux'
import BalloonWrapper from 'components/atoms/balloons/BalloonWrapper'
import { HouseUnlinkHandler } from 'components/features/pfm/HouseUnlinkHandler/HouseUnlinkHandler'
import { HouseDeleteHandler } from 'components/features/pfm/HouseDeleteHandler/HouseDeleteHandler'

export type HouseEditFormStateProps = {
  price: HomePrice | null
  addressValue: string | null
  isValueFieldActive: boolean
}

export type HouseEditFormOwnProps =
  & ReduxFormProps
  & {
    homeId: number | null
    onCancel: () => void
    onDelete: () => void
    onUnlink: () => void
  }

type HouseEditFormProps = HouseEditFormStateProps & HouseEditFormOwnProps

@reduxForm({
  form: FORM_HOMES_EDIT,
  asyncValidate: asyncValidator(schema),
  shouldAsyncValidate,
})
class HouseEditForm extends Component<HouseEditFormProps, {}> {

  render () {
    const {
      handleSubmit,
      onCancel,
      price,
      addressValue,
      isValueFieldActive,
      homeId,
      onDelete,
      onUnlink,
    } = this.props

    const priceBalloon = isValueFieldActive && price && addressValue ? (
      <BalloonWrapper arrowDirection='down'>
        <HomePriceBalloon price={ price } addressValue={ addressValue } />
      </BalloonWrapper>
    ) : null

    const editDeleteButtons = homeId && homeId !== -1 ? (
      <div className={ styles.buttonsRow }>
        <div className={ styles.button }>
          <HouseUnlinkHandler homeId={ homeId } onConfirm={ onUnlink }>
            <Button size='small' theme='lightBlue' qaMarker='unlink-button' fullWidth>
              Unlink
            </Button>
          </HouseUnlinkHandler>
        </div>
        <div className={ styles.button }>
          <HouseDeleteHandler homeId={ homeId } onConfirm={ onDelete }>
            <Button size='small' theme='lightBlue' qaMarker='delete-button' fullWidth>
              Delete
            </Button>
          </HouseDeleteHandler>
        </div>
      </div>
    ) : null

    const buttons = (
      <>
        <Button text='Save' submit { ...getSaveButtonFormProps } fullWidth />
        <div className={ styles.buttonRowWrapper }>
          { editDeleteButtons }
        </div>
        <Button
          text='Cancel'
          style='textOnly'
          onClick={ onCancel }
          fullWidth
          qaMarker='cancel-button'
        />
      </>
    )

    return (
      <form onSubmit={ handleSubmit } className={ styles.onSettingsPage }>
        <ul className={ cn(styles.form, styles.formHeight) }>
          <li className={ styles.item }>
            <Field
              name='address1'
              component={ TextFieldWrapped }
              props={{ ...FIELD_ADDRESS1_PROPS, autoFocus: true }}
            />
          </li>
          <li className={ styles.item }>
            <Field
              name='address2'
              component={ TextFieldWrapped }
              props={ FIELD_ADDRESS2_PROPS }
            />
          </li>
          <li className={ styles.item }>
            <CityStateField
              name='city'
            />
          </li>
          <li className={ styles.item }>
            <Field
              name='zip5'
              props={ FIELD_ZIP_PROPS }
              component={ TextFieldWrapped }
            />
          </li>
          <li className={ cn(styles.item, localStyles.dropdownHeight) }>
            <Field
              name='purchased_at'
              props={ FIELD_PURCHASED_PROPS }
              component={ SelectWrapped }
            />
          </li>
          <li className={ styles.item }>
            <Field
              name='estimated_value'
              props={ FIELD_HOME_ESTIMATED_PROPS }
              component={ TextFieldWrapped }
              { ...FIELD_CURRENCY_WRAPPED_PROPS }
            />
            { priceBalloon }
          </li>
        </ul>
        <div className={ styles.line }>
          <Field
            name='is_primary_residence'
            props={ FIELD_PRIMARY_RESIDENCE_PROPS }
            component={ CheckboxWrapped }
            parse={ parseValueToBoolean }
          />
        </div>
        <div className={ styles.submitItem }>
          { buttons }
        </div>
      </form>
    )
  }
}

const selector = getFormValues(FORM_HOMES_EDIT)

const mapStateToProps = (state: State): HouseEditFormStateProps => {
  const values = selector(state) as HomeFormValues
  const addressValue = values ? values.address1 : null

  const form = state.form[FORM_HOMES_EDIT]
  const activeField = form && form.active ? form.active : ''
  const isValueFieldActive = activeField === 'estimated_value'

  const { homePrices } = state.data.property
  const priceKey = values ? getHomePriceKey(values) : null
  const price = !priceKey || !(priceKey in homePrices) ? null : homePrices[priceKey]

  return { addressValue, price, isValueFieldActive }
}

const Container = connect<
  HouseEditFormStateProps,
  {},
  HouseEditFormOwnProps
>(mapStateToProps)(HouseEditForm)

// Deferred should fix bug when previous form is not destroyed but new one mounts
const ContainerDeferred = Deferred(Container)
export default ContainerDeferred
